/* eslint-disable no-mixed-spaces-and-tabs */
/* eslint-disable react/jsx-wrap-multilines */
import React, { useCallback, useEffect } from 'react';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router';
import { NAV_PAGE_LIST, TABLE_COLUMNS } from './constants';
import { useInjectSaga } from '../../utils/injectSaga';
import saga from './saga';
import { useInjectReducer } from '../../utils/injectReducer';
import reducer from './reducer';
import {
	getThermoboxesReportsSelector,
	getIsThermoboxesReportsLoadingSelector,
	getPaginationSelector,
	getThermoboxesOptionsSelector,
	getIsThermoboxesLoadingSelector,
	getModesOptionsSelector,
	getIsModesLoadingSelector,
} from './selectors';
import { getThermoboxesAction, getThermoboxesReportsAction, getModesAction, resetStateAction } from './actions';
import { getIsAvailableThermoboxesReportsButtonsSelector } from '../profile/selectors';
import { removeModalQuery } from '../../utils/removeModalQuery';
import { DataGridComponent } from '../../components/data-grid';
import { normalizeDataGridModel } from '../../utils/normalizeDataGridModel';
import { PageContentWithNav } from '../../components/page-content-with-nav';

const ThermoboxesReportsContainerWrapper = ({
	onGetThermoboxesReports,
	thermoboxesReports,
	isThermoboxesReportsLoading,
	pagination,
	onResetState,
	isAvailable,
	onGetThermoboxes,
	thermoboxesOptions,
	isThermoboxesLoading,
	onGetModes,
	modesOptions,
	isModesLoading,
}) => {
	useInjectSaga({ key: 'thermoboxesReportsContainerSaga', saga });
	useInjectReducer({ key: 'thermoboxesReportsContainerReducer', reducer });

	const { search, pathname } = useLocation();

	const query = new URLSearchParams(search);

	const history = useHistory();

	useEffect(() => {
		onGetThermoboxesReports({ searchQuery: removeModalQuery(query.toString()) });
		onGetThermoboxes();
		onGetModes();

		return () => onResetState();
	}, []);

	const handleAddButtonClick = () => {
		query.set('modal', 'thermobox_report');
		query.set('mode', 'add');

		history.push(`${pathname}?${query}`);
	};

	const handlePageChange = (_, { activePage }) => {
		query.set('page', activePage);

		history.push(`${pathname}?${query}`);

		onGetThermoboxesReports({ searchQuery: removeModalQuery(query.toString()) });
	};

	const handleSelectChange = (_, { value }) => {
		query.set('page', 1);

		if (value === 'all') {
			query.delete('per_page');
		} else {
			query.set('per_page', value);
		}

		history.push(`${pathname}?${query}`);

		onGetThermoboxesReports({ searchQuery: removeModalQuery(query.toString()) });
	};

	const handleSortChange = useCallback(sortModel => {
		const jsonSorts = JSON.stringify(sortModel);

		query.set('sorts', jsonSorts);

		history.push(`${pathname}?${query}`);

		onGetThermoboxesReports({ searchQuery: removeModalQuery(query.toString()) });
	});

	const handleFilterChange = useCallback((filterModel, action) => {
		if (action.reason) {
			const jsonFilters = JSON.stringify(
				normalizeDataGridModel(filterModel, TABLE_COLUMNS(thermoboxesOptions, modesOptions)),
			);

			query.delete('page');
			query.set('filters', jsonFilters);

			history.push(`${pathname}?${query}`);

			onGetThermoboxesReports({ searchQuery: removeModalQuery(query.toString()) });
		}
	});

	return (
		<PageContentWithNav
			pageNavigationItems={NAV_PAGE_LIST}
			isInnerRouter
			pageContent={
				<DataGridComponent
					headerText="Сводная таблица валидационных отчетов"
					columns={TABLE_COLUMNS(thermoboxesOptions, modesOptions)}
					onAddButtonClick={isAvailable.create ? handleAddButtonClick : null}
					rows={thermoboxesReports}
					pagination={
						!pagination
							? null
							: {
									...pagination,
									onPageChange: handlePageChange,
									onSelectChange: handleSelectChange,
							  }
					}
					isTableDataLoading={isThermoboxesReportsLoading || isThermoboxesLoading || isModesLoading}
					handleSortChange={handleSortChange}
					handleFilterChange={handleFilterChange}
				/>
			}
		/>
	);
};

const mapStateToProps = createStructuredSelector({
	thermoboxesReports: getThermoboxesReportsSelector(),
	isThermoboxesReportsLoading: getIsThermoboxesReportsLoadingSelector(),
	pagination: getPaginationSelector(),
	isAvailable: getIsAvailableThermoboxesReportsButtonsSelector(),
	thermoboxesOptions: getThermoboxesOptionsSelector(),
	isThermoboxesLoading: getIsThermoboxesLoadingSelector(),
	modesOptions: getModesOptionsSelector(),
	isModesLoading: getIsModesLoadingSelector(),
});

const mapDispatchToProps = {
	onGetThermoboxesReports: getThermoboxesReportsAction,
	onGetThermoboxes: getThermoboxesAction,
	onGetModes: getModesAction,
	onResetState: resetStateAction,
};

const ThermoboxesReportsContainer = connect(mapStateToProps, mapDispatchToProps)(ThermoboxesReportsContainerWrapper);

ThermoboxesReportsContainerWrapper.propTypes = {
	onGetThermoboxesReports: PropTypes.func.isRequired,
	thermoboxesReports: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number.isRequired,
			name: PropTypes.string,
			link: PropTypes.string.isRequired,
		}),
	),
	isThermoboxesReportsLoading: PropTypes.bool.isRequired,
	pagination: PropTypes.shape({
		currentPage: PropTypes.number,
		totalPages: PropTypes.number.isRequired,
		total: PropTypes.number,
		from: PropTypes.number,
		to: PropTypes.number,
		isNextItem: PropTypes.bool,
		isPrevItem: PropTypes.bool,
		isFirstItem: PropTypes.bool,
		isLastItem: PropTypes.bool,
	}),
	onResetState: PropTypes.func.isRequired,
	isAvailable: PropTypes.objectOf(PropTypes.bool),
	onGetThermoboxes: PropTypes.func.isRequired,
	thermoboxesOptions: PropTypes.array,
	isThermoboxesLoading: PropTypes.bool.isRequired,
	onGetModes: PropTypes.func.isRequired,
	modesOptions: PropTypes.array,
	isModesLoading: PropTypes.bool.isRequired,
};

export default ThermoboxesReportsContainer;
